import {Box} from "@mui/material";
import {ElevationApp} from "../ElevationApp";
import {EventEmitter2} from "../util/EventEmitter2";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {useFrmVar} from "../util/useFrmVar";
import {Sidebar, StripLabel} from "./Sidebar";
import {SidebarWarning, WarningsSection} from "./WarningsSection";
import {StilesSection} from "./StilesSection";
import {DoorLiteWarning} from "../model/door/MdDoorSpec";
import {MdDisplayView} from "../model/view/MdViewSettings";

// the door workspace's right sidebar: the door properties: the lite warnings (a lite
// over a stile line) and the stiles. Folds to a thin strip that calls out the warning
// count; the collapsed state is remembered in viewSettings.doorSidebarCollapsed.
export function DoorSidebar(props: {app: EventEmitter2<ElevationApp | null>}) {
    const app = useEventEmitter2(props.app);
    const workspace = useEventEmitter2(app?.viewSettings.workspace);
    const collapsed = useEventEmitter2(app?.viewSettings.doorSidebarCollapsed);
    const ready = useEventEmitter2(app?.ready);
    const activeView = useEventEmitter2(app?.viewSettings.activeView);

    if(!app || !ready || workspace !== "door") return null;

    return <DoorSidebarBody app={app} collapsed={!!collapsed} activeView={activeView ?? null} />;
}

function DoorSidebarBody(props: {app: ElevationApp; collapsed: boolean; activeView: MdDisplayView | null}) {
    const app = props.app;
    const door = app.doorSpec;

    // re-render as lites, stiles and leaf sizes change, so the warnings track them.
    useEventEmitter2(door.litesChanged);
    useFrmVar(door.stileSide);
    useFrmVar(door.stileTop);
    useFrmVar(door.stileBottom);
    useFrmVar(door.actualHeight);
    useFrmVar(door.activeDoorWidth);
    useFrmVar(door.inActiveDoorWidth);

    const isPair = app.frameSpec.isPair;

    // select the lite and, when the other leaf's door view is up, switch to its leaf.
    const highlight = (w: DoorLiteWarning) => {
        const view: MdDisplayView = w.leaf === "active" ? "door-active" : "door-inactive";
        if(props.activeView !== view) app.showView(view);
        app.viewSettings.selectItem({type: "door-lite", id: w.liteId, leaf: w.leaf}, false);
        app.viewSettings.activeTab.emit("selected");
    };

    const warnings: SidebarWarning[] = door.liteWarnings(app.draw.formatDimension).map(w => ({
        key: w.liteId,
        title: isPair ? `${w.mark} (${w.leaf} leaf) crosses the stile` : `${w.mark} crosses the stile`,
        detail: w.message,
        onClick: () => highlight(w),
    }));

    // the folded strip calls out the warnings when there are any; otherwise it just
    // names the sidebar.
    const strip = warnings.length > 0 ? (
        <>
            <Box sx={{
                mt: 1,
                minWidth: 18,
                height: 18,
                px: 0.5,
                borderRadius: 9,
                bgcolor: "error.main",
                color: "error.contrastText",
                fontSize: 11,
                lineHeight: "18px",
                textAlign: "center",
                userSelect: "none",
            }}>
                {warnings.length}
            </Box>
            <StripLabel color="error.main">Warnings</StripLabel>
        </>
    ) : (
        <StripLabel>Properties</StripLabel>
    );

    return (
        <Sidebar
            name="Properties"
            strip={strip}
            title="Properties"
            collapsed={props.collapsed}
            onCollapsedChange={value => app.viewSettings.doorSidebarCollapsed.emit(value)}
        >
            <WarningsSection warnings={warnings} />
            <StilesSection app={app} />
        </Sidebar>
    );
}
